import React, { useEffect, useState } from 'react';
import { Clock, Trophy, Plus, Pencil, Trash2 } from 'lucide-react';

import Button from '../shared/Button';
import ProgramRegistrationModal from './ProgramRegistrationModal';
import ProgramAdminFormModal, {
  defaultProgramForm,
  formToProgramPayload,
  programToForm,
} from './ProgramAdminFormModal';
import ConfirmActionModal from '../shared/ConfirmActionModal';
import axiosInstance from '../../api/axiosInstance';
import { programsAPI } from '../../api/programs';
import { useAuth } from '../../contexts/AuthContext';

const ProgramDetailsModal = ({ program, onClose, onRegisterClick }) => {
  const features = [
    { text: program.feature1, color: program.feature1_color },
    { text: program.feature2, color: program.feature2_color },
    { text: program.feature3, color: program.feature3_color },
    { text: program.feature4, color: program.feature4_color },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-xl w-full relative overflow-hidden">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors">✕</button>

        <div className="bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] p-6 text-white">
          <h3 className="text-2xl font-bold mb-2">{program.title}</h3>
          <div className="flex items-center text-white/90">
            <Clock className="w-4 h-4 mr-2" />
            <span>{program.duration}</span>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {program.about_program && (
            <div>
              <h4 className="text-xl font-bold mb-3">{program.about_program_title || 'О программе'}</h4>
              <p className="text-gray-600">{program.about_program}</p>
            </div>
          )}

          <div>
            <h4 className="text-xl font-bold mb-3">{program.features_title || 'Особенности программы'}</h4>
            <ul className="space-y-2">
              {features.map((feature, index) =>
                feature.text ? (
                  <li key={index} className="flex items-center">
                    <div className="w-2 h-2 rounded-full mr-3" style={{ backgroundColor: feature.color }} />
                    <span className="text-gray-600">{feature.text}</span>
                  </li>
                ) : null,
              )}
            </ul>
          </div>

          <div className="pt-6 border-t">
            <Button onClick={onRegisterClick} className="w-full" withArrow>
              {program.registration_button_text || 'Записаться на консультацию'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProgramCard = ({ program, isAdmin, onEdit, onDelete }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);

  const features = [
    { text: program.feature1, color: '#662d91' },
    { text: program.feature2, color: '#4897d2' },
  ];

  return (
    <>
      <div className="relative h-full">
        <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 h-full flex flex-col">
          <div className="w-14 h-14 rounded-lg bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] p-0.5">
            <div className="w-full h-full bg-white rounded-lg flex items-center justify-center">
              <Trophy className="w-8 h-8 text-[#662d91]" />
            </div>
          </div>

          <h3 className="text-2xl font-bold mt-4 mb-4 min-h-[2.5rem]">{program.title}</h3>

          <div className="mb-6 min-h-[4rem]">
            <ul className="space-y-3">
              {features.map((feature, index) =>
                feature.text ? (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 rounded-full mr-3 mt-2" style={{ backgroundColor: feature.color }} />
                    <span className="text-sm text-gray-600 leading-relaxed">{feature.text}</span>
                  </li>
                ) : null,
              )}
            </ul>
          </div>

          <div className="mt-auto">
            <Button onClick={() => setShowDetails(true)} withArrow className="w-full">Подробнее</Button>
          </div>
        </div>

        {isAdmin && (
          <div className="absolute top-2 right-2 z-20 flex gap-2">
            <button type="button" onClick={() => onEdit(program)} className="p-2 rounded-full bg-white/90 text-blue-700 hover:bg-blue-50 shadow" title="Редактировать программу">
              <Pencil className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => onDelete(program.id)} className="p-2 rounded-full bg-white/90 text-red-600 hover:bg-red-50 shadow" title="Удалить программу">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {showDetails && (
        <ProgramDetailsModal
          program={program}
          onClose={() => setShowDetails(false)}
          onRegisterClick={() => {
            setShowDetails(false);
            setShowRegistration(true);
          }}
        />
      )}
      {showRegistration && <ProgramRegistrationModal program={program} onClose={() => setShowRegistration(false)} />}
    </>
  );
};

const ProgramCatalogPage = () => {
  const [programs, setPrograms] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { isAdmin } = useAuth();
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingProgramId, setEditingProgramId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [programForm, setProgramForm] = useState(defaultProgramForm);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    description: '',
    actionType: null,
    payload: null,
  });

  const loadPrograms = async () => {
    try {
      const response = await axiosInstance.get('trainings/');
      setPrograms(response.data || []);
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError('Не удалось загрузить программы.');
    } finally {
      setLoading(false);
    }
  };

  const loadCoaches = async () => {
    try {
      const response = await axiosInstance.get('/team-members/?is_active=true');
      const list = (response.data || []).filter((item) => ['head_coach', 'coach', 'nutrition'].includes(item.role));
      setCoaches(list);
    } catch (err) {
      console.error('Error fetching coaches:', err);
    }
  };

  useEffect(() => {
    loadPrograms();
    if (isAdmin) {
      loadCoaches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const openCreateModal = () => {
    setEditingProgramId(null);
    setProgramForm(defaultProgramForm);
    setShowFormModal(true);
  };

  const openEditModal = (program) => {
    setEditingProgramId(program.id);
    setProgramForm(programToForm(program));
    setShowFormModal(true);
  };

  const handleSubmitProgram = async () => {
    if (!programForm.title) {
      window.alert('Название программы обязательно.');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = formToProgramPayload(programForm);
      if (editingProgramId) {
        await programsAPI.updateProgram(editingProgramId, payload);
      } else {
        await programsAPI.createProgram(payload);
      }
      setShowFormModal(false);
      setEditingProgramId(null);
      setProgramForm(defaultProgramForm);
      setLoading(true);
      await loadPrograms();
    } catch (err) {
      console.error('Error saving program:', err);
      window.alert('Не удалось сохранить программу.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProgram = async (programId) => {
    try {
      await programsAPI.deleteProgram(programId);
      setLoading(true);
      await loadPrograms();
    } catch (err) {
      console.error('Error deleting program:', err);
      window.alert('Не удалось удалить программу.');
    }
  };

  const openConfirm = ({ title, description, actionType, payload = null }) => {
    setConfirmModal({ isOpen: true, title, description, actionType, payload });
  };

  const closeConfirm = () => {
    setConfirmModal({ isOpen: false, title: '', description: '', actionType: null, payload: null });
  };

  const confirmAction = async () => {
    const { actionType, payload } = confirmModal;
    closeConfirm();
    if (actionType === 'save') {
      await handleSubmitProgram();
      return;
    }
    if (actionType === 'delete' && payload?.programId) {
      await handleDeleteProgram(payload.programId);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#662d91]"></div>
      </div>
    );
  }

  if (error) {
    return <div className="pt-24 text-center text-red-600 py-8">{error}</div>;
  }

  return (
    <div className="min-h-screen pt-24 bg-gray-50 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <h1 className="text-3xl font-bold text-gray-900">Все программы</h1>
          {isAdmin && (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4]"
            >
              <Plus className="w-4 h-4" />
              Добавить программу
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {programs.map((program) => (
            <ProgramCard
              key={program.id}
              program={program}
              isAdmin={isAdmin}
              onEdit={openEditModal}
              onDelete={(programId) => openConfirm({
                title: 'Подтвердите удаление',
                description: 'Удалить программу?',
                actionType: 'delete',
                payload: { programId },
              })}
            />
          ))}
        </div>
      </div>

      <ProgramAdminFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSubmit={(e) => {
          e.preventDefault();
          openConfirm({
            title: editingProgramId ? 'Подтвердите изменение' : 'Подтвердите добавление',
            description: editingProgramId ? 'Сохранить изменения программы?' : 'Добавить новую программу?',
            actionType: 'save',
          });
        }}
        isSubmitting={isSubmitting}
        editingProgramId={editingProgramId}
        programForm={programForm}
        setProgramForm={setProgramForm}
        coaches={coaches}
      />

      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        description={confirmModal.description}
        onConfirm={confirmAction}
        onCancel={closeConfirm}
        loading={isSubmitting}
      />
    </div>
  );
};

export default ProgramCatalogPage;
